import * as Accordion from "#root/components/accordion";
import { Case, Switch } from "#root/components/conditionals";
import * as Fields from "#root/components/form/fields";
import { GlyphLoading } from "#root/components/glyphs";
import Sidebar from "#root/components/sidebar";
import StandardEditor, { type StandardEditorProps } from "#root/components/standard-editor";
import {
  getNewsRPMInstanceFeedCollection,
  type NewsRPMInstanceFeedDetailItem,
  NewsRPMInstanceFeedDetailItemSchema,
  type NewsRPMInstanceFeedListItem,
  NewsRPMInstanceFeedListItemSchema
} from "#root/schema/platform/newsrpm-instance";
import { useData } from "#root/utility/hooks";
import { mergeUpdate } from "#root/utility/merge-update";
import type { RecordModificationResponse } from "#root/utility/remote-data";
import { flatToNamedTree } from "#root/utility/tree";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import PageLayout from "./layout";

export default function ({ instanceId, feedId: id }): ReactNode {
  const navigate = useNavigate();
  const feedCollection = useMemo(() => getNewsRPMInstanceFeedCollection(instanceId), [instanceId]);

  const [feeds, setFeeds] = useData(async () => {
    const { rows } = await feedCollection.list();
    return rows;
  }, [feedCollection, instanceId]);

  const handleUpdate = useCallback(
    (data: RecordModificationResponse<NewsRPMInstanceFeedDetailItem>) => {
      setFeeds(mergeUpdate(feeds!, data));

      if ("deleted" in data) {
        navigate(`/admin/platform/newsrpm-instance/${instanceId}/feed`);
      } else if ("created" in data) {
        navigate(`/admin/platform/newsrpm-instance/${instanceId}/feed/${data.created}`);
      }
    },
    [navigate, instanceId, feeds, setFeeds],
  );

  if (!feeds) {
    return (
      <PageLayout title="Feed">
        <section>
          <GlyphLoading/>
        </section>
      </PageLayout>
    );
  }

  return (
    <PageLayout title="Admin - Feed">
      <FeedSidebar instanceId={instanceId} feeds={feeds} id={id}/>
      <section>
        <Switch>
          <Case expr={id === "new"}>
            <h1>New Feed</h1>
            <FeedEditor collection={feedCollection} onSave={handleUpdate}/>
          </Case>
          <Case expr={id}>
            <h1>Edit Feed</h1>
            <FeedEditor id={id} collection={feedCollection} onSave={handleUpdate} onDelete={handleUpdate}/>
          </Case>
        </Switch>
      </section>
    </PageLayout>
  );
}

function FeedSidebar({ instanceId, feeds, id }: { instanceId: string; feeds: NewsRPMInstanceFeedListItem[]; id: string | null }) {
  const navigate = useNavigate();
  const categories = useMemo(() => flatToNamedTree(feeds, f => f.type, f => f.name), [feeds]);

  return (
    <Sidebar>
      <button className="flat center" type="button" onClick={() => navigate(`/admin/platform/newsrpm-instance/${instanceId}/feed/new`)}>
        New Feed
      </button>
      <Accordion.Parent initialItemId={id}>
        {categories.map(({ name, items }) => (
          <Accordion.Group key={name} label={name}>
            {items.map(item => (
              <Accordion.Item key={item.id} itemId={item.id}>
                <Link to={`/admin/platform/newsrpm-instance/${instanceId}/feed/${item.id}`}>{item.name}</Link>
              </Accordion.Item>
            ))}
          </Accordion.Group>
        ))}
      </Accordion.Parent>
    </Sidebar>
  );
}

function FeedEditor(props: StandardEditorProps<typeof NewsRPMInstanceFeedListItemSchema, typeof NewsRPMInstanceFeedDetailItemSchema>) {
  return (
    <StandardEditor {...props}>
      <Fields.Set name="name">
        <label>Feed Name</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="title">
        <label>Title</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="description">
        <label>Description</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
      <Fields.Set name="link">
        <label>Link</label>
        <Fields.String/>
        <Fields.Validity/>
      </Fields.Set>
    </StandardEditor>
  );
}
